import { selectedLayerIdsAtom, setLayersAtom } from '@/atoms';
import { LayerData } from '@/atoms/layers.atom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Pane } from '@/components/ui/pane';
import { execute } from '@/graphql/execute';
import { FeaturesQuery, Project } from '@/graphql/graphql';
import { useTheme } from '@/hooks/useTheme';
import PageSkeleton from '@/layout/Skeleton';
import { featuresByLayerQuery, featuresPaginatedQuery } from '@/queries/feature.query';
import { layersQuery } from '@/queries/layer.query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { FeatureCollection, Geometry } from 'geojson';
import { useAtom } from 'jotai';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Suspense, lazy, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Layer, Map as Mapbox, Source } from 'react-map-gl/mapbox';
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { Table } from './Table';
import IconFull from './icons/Full';
import IconOneByOneHorizontal from './icons/OneByOneHorizontal';
import IconOneByOneVertical from './icons/OneByOneVertical';
import IconTwoByOneHorizontal from './icons/TwoByOneHorizontal';
import IconTwoByOneVertical from './icons/TwoByOneVertical';

const Viewer = lazy(() => import('@/components/Viewer'));

// --- Layout types ---

interface LayoutSplit {
  direction: 'horizontal' | 'vertical';
  defaultSizes?: number[];
  children: LayoutNode[];
}

type LayoutNode = 'viewer' | 'table' | 'map' | LayoutSplit;

interface LayoutConfig {
  label: string;
  icon: ReactNode;
  layout: LayoutNode;
}

// --- Layout definitions ---

const LAYOUTS: Record<string, LayoutConfig> = {
  full: {
    label: 'Full table',
    icon: <IconFull />,
    layout: 'table',
  },
  '1-1-vertical-split': {
    label: 'Viewer and table (1:1)',
    icon: <IconOneByOneVertical />,
    layout: {
      direction: 'vertical',
      defaultSizes: [50, 50],
      children: ['viewer', 'table'],
    },
  },
  '2-1-vertical-split': {
    label: 'Map and viewer and table (2:1:1)',
    icon: <IconTwoByOneVertical />,
    layout: {
      direction: 'vertical',
      defaultSizes: [50, 50],
      children: [
        {
          direction: 'horizontal',
          children: ['map', 'viewer'],
        },
        'table',
      ],
    },
  },
  '1-1-horizontal-split': {
    label: 'Viewer and table (1:1)',
    icon: <IconOneByOneHorizontal />,
    layout: {
      direction: 'vertical',
      children: [
        {
          direction: 'horizontal',
          children: ['viewer', 'table'],
        },
      ],
    },
  },
  '2-1-horizontal-split': {
    label: 'Map and viewer and table (2:1)',
    icon: <IconTwoByOneHorizontal />,
    layout: {
      direction: 'horizontal',
      children: [
        {
          direction: 'vertical',
          children: ['viewer', 'map'],
        },
        'table',
      ],
    },
  },
};

const LAYOUT_KEYS = Object.keys(LAYOUTS);
const DEFAULT_LAYOUT = 'full';

// --- Viewer/Map wrappers that show only the selected layer ---

function useOverrideSelectedLayers(projectId: string, layerId: string, layers: LayerData[]) {
  const [, setLayers] = useAtom(setLayersAtom);
  const [selectedIds, setSelectedIds] = useAtom(selectedLayerIdsAtom(projectId));
  const previousIdsRef = useRef<string[]>(selectedIds);

  useEffect(() => {
    previousIdsRef.current = selectedIds;
  }, []);

  useEffect(() => {
    if (layers.length > 0) setLayers(layers);
  }, [layers, setLayers]);

  useEffect(() => {
    setSelectedIds([layerId]);
    return () => {
      setSelectedIds(previousIdsRef.current);
    };
  }, [layerId, setSelectedIds]);
}

function AttributeViewer({ projectId, layerId, layers }: { projectId: string; layerId: string; layers: LayerData[] }) {
  useOverrideSelectedLayers(projectId, layerId, layers);

  return (
    <div className='flex flex-col flex-1 overflow-hidden'>
      <ErrorBoundary
        fallback={
          <div className='flex items-center justify-center flex-1 text-sm text-muted-foreground'>
            Failed to load viewer
          </div>
        }>
        <Suspense fallback={null}>
          <Viewer projectId={projectId} hideMap />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

function AttributeMap({ projectId, layerId, layer }: { projectId: string; layerId: string; layer?: LayerData }) {
  const theme = useTheme();

  const isTileLayer = layer?.type === 'TILE';
  const isVectorLayer = layer?.type === 'VECTOR';

  const { data: featuresData } = useQuery({
    queryKey: ['features', layerId],
    queryFn: () => execute(featuresByLayerQuery, { layerIds: [layerId] }),
    enabled: isVectorLayer,
  });

  const featureCollection = useMemo((): FeatureCollection | null => {
    if (!featuresData?.featuresByLayer?.length) return null;
    return {
      type: 'FeatureCollection',
      features: featuresData.featuresByLayer.map((f: FeaturesQuery['featuresByLayer'][number]) => ({
        type: 'Feature' as const,
        geometry: f.geometryWGS84 as Geometry,
        properties: { ...f.properties, id: f.id },
      })),
    };
  }, [featuresData]);

  const mapStyle = theme === 'light' ? 'mapbox://styles/mapbox/streets-v12' : 'mapbox://styles/mapbox/dark-v11';

  const bounds = useMemo(() => {
    if (layer?.bboxNew?.coords) {
      return [...layer.bboxNew.coords[0].slice(0, 2), ...layer.bboxNew.coords[1].slice(0, 2)] as [
        number,
        number,
        number,
        number,
      ];
    }
    return undefined;
  }, [layer]);

  return (
    <div className='flex flex-col flex-1 overflow-hidden'>
      <ErrorBoundary
        fallback={
          <div className='flex items-center justify-center flex-1 text-sm text-muted-foreground'>
            Failed to load map
          </div>
        }>
        <Mapbox
          mapboxAccessToken='pk.eyJ1IjoiYWN0ZWdvbiIsImEiOiJjbWJ3MW8ycW0wdW04MmpzMDE4d3g2YXh4In0.iV9tT4QGO3U112GMEyn2Cg'
          initialViewState={{
            bounds,
            fitBoundsOptions: { padding: 40 },
          }}
          style={{ width: '100%', height: '100%' }}
          mapStyle={mapStyle}
          transformRequest={(url) => ({
            url,
            credentials: url.includes('skygis.app') ? 'include' : undefined,
          })}>
          {isTileLayer && layer && (
            <Source
              id={layer.id}
              type='raster'
              tiles={[`${import.meta.env.VITE_RESOURCES_ENDPOINT}/${layer.root}/{z}/{x}/{y}.webp`]}
              tileSize={layer.metadata?.tiffSettings?.tileSize || 256}
              scheme={layer.metadata?.tiffSettings?.schema || 'xyz'}
              minzoom={layer.metadata?.tiffSettings?.minZoom || 0}
              maxzoom={layer.metadata?.tiffSettings?.maxZoom || 22}
              bounds={bounds}>
              <Layer id={layer.id} type='raster' paint={{ 'raster-opacity': 0.8 }} />
            </Source>
          )}
          {isVectorLayer && featureCollection && (
            <Source id='geojson' type='geojson' data={featureCollection}>
              <Layer
                id='line-layer'
                type='line'
                source='geojson'
                filter={['any', ['==', ['geometry-type'], 'LineString'], ['==', ['geometry-type'], 'MultiLineString']]}
                paint={{
                  'line-color': theme === 'light' ? '#334155' : '#e2e8f0',
                  'line-width': 2,
                }}
              />
              <Layer
                id='fill-layer'
                type='fill'
                source='geojson'
                filter={['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']]}
                paint={{
                  'fill-color': theme === 'light' ? '#334155' : '#e2e8f0',
                  'fill-opacity': 0.3,
                  'fill-outline-color': theme === 'light' ? '#334155' : '#e2e8f0',
                }}
              />
              <Layer
                id='point-layer'
                type='circle'
                source='geojson'
                filter={['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']]}
                paint={{
                  'circle-color': theme === 'light' ? '#334155' : '#e2e8f0',
                  'circle-radius': 5,
                  'circle-stroke-color': '#fff',
                  'circle-stroke-width': 1,
                }}
              />
            </Source>
          )}
        </Mapbox>
      </ErrorBoundary>
    </div>
  );
}

// --- Recursive renderer ---

interface RenderContext {
  tableProps: Record<string, unknown>;
  projectId: string;
  layerId: string;
  layers: LayerData[];
}

function renderNode(node: LayoutNode, ctx: RenderContext, key: string): ReactNode {
  if (node === 'table') {
    return <Table {...(ctx.tableProps as any)} />;
  }
  if (node === 'viewer') {
    return <AttributeViewer projectId={ctx.projectId} layerId={ctx.layerId} layers={ctx.layers} />;
  }
  if (node === 'map') {
    return (
      <AttributeMap
        projectId={ctx.projectId}
        layerId={ctx.layerId}
        layer={ctx.layers.find((l) => l.id === ctx.layerId)}
      />
    );
  }

  return (
    <Pane direction={node.direction} defaultSizes={node.defaultSizes} persistenceKey={key}>
      {node.children.map((child, i) => renderNode(child, ctx, `${key}-${i}`))}
    </Pane>
  );
}

// --- Component ---

export const AttributeTable = () => {
  const { layerId } = useParams();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { project, projectLoading, href } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    href: string;
  }>();
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const _handleSelect = (selection: string[]) => {
    setSelectedFeatures(selection);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['layers', project?.id],
    queryFn: () => execute(layersQuery, { projectId: project?.id }),
  });

  const layer = data?.layers?.find((l) => l.id === layerId);
  const layers = data?.layers ?? [];

  const layoutKey = LAYOUT_KEYS.includes(searchParams.get('layout') ?? '')
    ? searchParams.get('layout')!
    : DEFAULT_LAYOUT;
  const layout = LAYOUTS[layoutKey];

  const setLayout = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === DEFAULT_LAYOUT) {
      next.delete('layout');
    } else {
      next.set('layout', key);
    }
    setSearchParams(next, { replace: true });
  };

  const tableProps = {
    projectId: project?.id!,
    layerId: layerId!,
    selection: selectedFeatures,
    onSelect: _handleSelect,
    layers,
  };

  return (
    <PageSkeleton
      steps={[
        { label: projectLoading ? 'Loading...' : project.name, href },
        { label: 'Attributes', href: `${href}/layers` },
        {
          label: isLoading ? 'Loading...' : layer?.name || 'Layers',
          href: `${href}/layers/${layerId}/attributes`,
          options: layers.map((l) => ({
            label: l.name,
            href: `${href}/layers/${l.id}/attributes`,
            onPrefetch: () => {
              queryClient.prefetchInfiniteQuery({
                queryKey: ['features-paginated', project?.id, l.id, undefined, undefined, undefined],
                queryFn: () =>
                  execute(featuresPaginatedQuery, {
                    projectId: project?.id!,
                    layerId: l.id,
                    limit: 100,
                  }),
                staleTime: 30_000,
                initialPageParam: undefined as string | undefined,
              });
            },
          })),
        },
      ]}
      actions={[
        {
          element: (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className='size-7 flex items-center justify-center hover:bg-slate-100 active:bg-slate-200/70 rounded-md text-slate-500 hover:text-slate-700 [&>svg]:size-4 [&>svg]:stroke-[2.25px] dark:text-stone-100 dark:hover:text-stone-100 dark:hover:bg-stone-800/50 dark:active:bg-stone-800/50'>
                  {layout.icon}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {LAYOUT_KEYS.map((key) => {
                  const config = LAYOUTS[key];
                  const Icon = config.icon;
                  return (
                    <DropdownMenuItem key={key} onClick={() => setLayout(key)}>
                      {Icon}
                      {config.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          ),
          tooltip: 'Layout',
        },
      ]}>
      {renderNode(layout.layout, { tableProps, projectId: project?.id!, layerId: layerId!, layers }, `attr-pane-${layoutKey}`)}
    </PageSkeleton>
  );
};
